import { MenuItem, Stack, TextField } from "@mui/material";
import RemoveIconButton from "./RemoveIconButton";
import {
  literalValue,
  VARIABLES,
  variableSpec,
  type LeafCondition,
  type Operator,
  type Variable,
  type WizardParameter,
} from "./clearanceRulesConfig";
import ValueField from "./ValueField";
import { classificationOptions } from "./axisValueLabels";
import type { LidarType } from "../schemas";
import t from "../translations";

const OPERATOR_LABELS: Record<Operator, string> = {
  less_than: t.OperatorLessThan,
  less_than_or_equal_to: t.OperatorLessThanOrEqualTo,
  greater_than: t.OperatorGreaterThan,
  greater_than_or_equal_to: t.OperatorGreaterThanOrEqualTo,
  equal_to: t.OperatorEqualTo,
};

const VARIABLE_LABELS: Record<Variable, string> = {
  powerline_distance: t.VariablePowerlineDistance,
  powerline_distance_horizontal: t.VariablePowerlineDistanceHorizontal,
  powerline_distance_vertical: t.VariablePowerlineDistanceVertical,
  height_above_ground: t.VariableHeightAboveGround,
  position_along_span: t.VariablePositionAlongSpan,
  span_length: t.VariableSpanLength,
  centre_line_distance: t.VariableCentreLineDistance,
  centre_line_distance_horizontal: t.VariableCentreLineDistanceHorizontal,
  centre_line_distance_vertical: t.VariableCentreLineDistanceVertical,
  outer_phase_distance_horizontal: t.VariableOuterPhaseDistanceHorizontal,
  classification: t.VariableClassification,
};

type Props = {
  condition: LeafCondition;
  parameters: WizardParameter[];
  // The region's own point classification codes - what "classification"
  // conditions offer.
  lidarTypes: LidarType[];
  onChange: (condition: LeafCondition) => void;
  onRemove: () => void;
  disableRemove?: boolean;
};

export default function LeafConditionRow({
  condition,
  parameters,
  lidarTypes,
  onChange,
  onRemove,
  disableRemove,
}: Props) {
  const spec = variableSpec(condition.variable);

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <TextField
        select
        size="small"
        label={t.VariableLabel}
        value={condition.variable}
        onChange={(event) => {
          const variable = event.target.value as Variable;
          const nextSpec = variableSpec(variable);
          // Fall back to the new variable's first allowed operator if its
          // current one isn't valid there, and always reset the value.
          const operators: readonly Operator[] = nextSpec.operators;
          onChange({
            ...condition,
            variable,
            operator: operators.includes(condition.operator) ? condition.operator : operators[0],
            value: literalValue(),
          });
        }}
        sx={{ width: 240 }}
      >
        {VARIABLES.map(({ name }) => (
          <MenuItem key={name} value={name}>
            {VARIABLE_LABELS[name]}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        label={t.OperatorLabel}
        value={condition.operator}
        onChange={(event) =>
          onChange({ ...condition, operator: event.target.value as Operator })
        }
        disabled={spec.operators.length === 1}
        sx={{ width: 200 }}
      >
        {spec.operators.map((operator) => (
          <MenuItem key={operator} value={operator}>
            {OPERATOR_LABELS[operator]}
          </MenuItem>
        ))}
      </TextField>
      <ValueField
        label={t.ValueLabel}
        value={condition.value}
        parameters={parameters}
        onChange={(value) => onChange({ ...condition, value })}
        literalOptions={spec.valueKind === "enum" ? classificationOptions(lidarTypes) : undefined}
      />
      <RemoveIconButton
        aria-label={t.RemoveConditionButton}
        onClick={onRemove}
        disabled={disableRemove}
      />
    </Stack>
  );
}
